class LoginPage {
  private usernameInput: HTMLInputElement | null = null;
  private passwordInput: HTMLInputElement | null = null;
  private submitButton: HTMLInputElement | null = null;
  private togglePasswordButton: HTMLButtonElement | null = null;

  constructor() {
    this._init();
  }

  _init(): void {
    this._getFormElements();
    this._createPasswordToggle();
    this._setupEventListeners();
    this._updateSubmitButtonState();
  }

  /**
   * Récupère les éléments du formulaire de connexion
   */
  private _getFormElements(): void {
    // Sélecteurs pour les champs de connexion WordPress
    this.usernameInput = document.querySelector(
      'input[name="log"]'
    ) as HTMLInputElement;
    this.passwordInput = document.querySelector(
      'input[name="pwd"]'
    ) as HTMLInputElement;
    this.submitButton = document.querySelector(
      'input[name="wp-submit"]'
    ) as HTMLInputElement;
  }

  /**
   * Crée le bouton de toggle du mot de passe
   */
  private _createPasswordToggle(): void {
    if (!this.passwordInput) return;

    const passwordWrapper = this.passwordInput.parentElement;
    if (!passwordWrapper) return;

    // Ajouter une classe wrapper pour positionner l'icône
    passwordWrapper.classList.add('login-password-wrapper');

    // Créer le bouton toggle
    this.togglePasswordButton = document.createElement('button');
    this.togglePasswordButton.type = 'button';
    this.togglePasswordButton.className = 'login-password-toggle';
    this.togglePasswordButton.setAttribute(
      'aria-label',
      'Afficher le mot de passe'
    );

    // Icône œil fermé (masqué)
    const eyeOffIcon = `
      <svg class="eye-icon eye-off" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.99902 3L20.999 21M9.8433 9.91364C9.32066 10.4536 8.99902 11.1892 8.99902 12C8.99902 13.6569 10.3422 15 11.999 15C12.8215 15 13.5667 14.669 14.1086 14.133M6.49902 6.64715C4.59972 7.90034 3.15305 9.78394 2.45703 12C3.73128 16.0571 7.52159 19 11.9992 19C13.9881 19 15.8414 18.4194 17.3988 17.4184M10.999 5.04939C11.328 5.01673 11.6617 5 11.9992 5C16.4769 5 20.2672 7.94291 21.5414 12C21.2607 12.894 20.8577 13.7338 20.3522 14.5" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    // Icône œil ouvert (visible)
    const eyeOnIcon = `
      <svg class="eye-icon eye-on" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    this.togglePasswordButton.innerHTML = eyeOffIcon + eyeOnIcon;

    // Insérer le bouton après le champ password
    passwordWrapper.appendChild(this.togglePasswordButton);
  }

  /**
   * Configure les écouteurs d'événements
   */
  private _setupEventListeners(): void {
    if (this.usernameInput) {
      this.usernameInput.addEventListener('input', () =>
        this._updateSubmitButtonState()
      );
      this.usernameInput.addEventListener('blur', () =>
        this._updateSubmitButtonState()
      );
    }

    if (this.passwordInput) {
      this.passwordInput.addEventListener('input', () =>
        this._updateSubmitButtonState()
      );
      this.passwordInput.addEventListener('blur', () =>
        this._updateSubmitButtonState()
      );
    }

    if (this.togglePasswordButton) {
      this.togglePasswordButton.addEventListener('click', () =>
        this._togglePasswordVisibility()
      );
    }
  }

  /**
   * Toggle la visibilité du mot de passe
   */
  private _togglePasswordVisibility(): void {
    if (!this.passwordInput || !this.togglePasswordButton) return;

    const isPassword = this.passwordInput.type === 'password';

    if (isPassword) {
      this.passwordInput.type = 'text';
      this.togglePasswordButton.classList.add('visible');
      this.togglePasswordButton.setAttribute(
        'aria-label',
        'Masquer le mot de passe'
      );
    } else {
      this.passwordInput.type = 'password';
      this.togglePasswordButton.classList.remove('visible');
      this.togglePasswordButton.setAttribute(
        'aria-label',
        'Afficher le mot de passe'
      );
    }
  }

  /**
   * Met à jour l'état disabled du bouton submit
   */
  private _updateSubmitButtonState(): void {
    if (!this.submitButton) return;

    const usernameValue = this.usernameInput?.value.trim() || '';
    const passwordValue = this.passwordInput?.value.trim() || '';

    const isFormValid = usernameValue.length > 0 && passwordValue.length > 0;

    if (isFormValid) {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.classList.remove('disabled');
    } else {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.classList.add('disabled');
    }
  }
}

export const loginPage = new LoginPage();
